import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { giornoEMese, leggiDataUtc, migliaia } from '../../utils/percorso';
import Icona from '../cliente/Icone';

// Impostazioni → Estrattore (e /admin/estrattore, che porta qui): "Estrattore: attivo / fermo da X min", quanti in
// coda e letti oggi, quale estensione, captcha da risolvere e letture non riuscite (si aprono
// sulla card dell'immobile nella scheda del cliente), come installare l'estensione.
// Sotto c'è anche lo stato della ricerca per area (backend/verifica_area.py): se i portali capiscono il poligono
// disegnato sulla mappa dai clienti.

const minuti = (n) => (n < 60 ? `${n} min` : n < 60 * 48 ? `${Math.floor(n / 60)} h` : `${Math.floor(n / 1440)} giorni`);
const linkImmobile = (i) => `/admin/clienti/${i.cliente_id}?evidenzia=${encodeURIComponent(i.id)}#immobili`;

export const rigaEstrattore = (stato) => {
  if (!stato.configurato) return 'non collegato (manca ESTRATTORE_TOKEN sul server)';
  if (stato.ultimo_contatto == null) return 'mai collegato';
  if (!stato.attivo) return `fermo da ${minuti(stato.minuti_da_ultimo_contatto)}`;
  if (stato.captcha > 0) return 'attivo · captcha da risolvere nel Chrome dell\'ufficio';
  if (stato.stato === 'limite_giornaliero') return `attivo · limite del giorno raggiunto (${stato.oggi}/${stato.limite})`;
  if (stato.stato === 'in_pausa') return 'acceso ma in pausa (estrazione automatica spenta)';
  return stato.in_corso ? 'attivo · sta leggendo un annuncio' : 'attivo';
};

// Come installare l'estensione (docs/estensione.md in breve) e lo .zip per un secondo computer
export const IstruzioniEstensione = () => (
  <div className="imp-istruzioni" data-testid="istruzioni-estensione">
    <b>Estensione Chrome dell'estrattore</b>
    <p className="imp-nota">Legge gli annunci in coda dal Chrome dell'ufficio, uno alla volta (da 10 a 300 pagine al giorno, 80 di base, con pause).</p>
    <ol>
      <li><a href="/estensione/mkclient-estensione.zip" download>Scaricate lo .zip</a> e scompattatelo in una cartella che resta lì (es. Documenti/mkclient-estensione).</li>
      <li>In Chrome aprite <code>chrome://extensions</code> e accendete <b>Modalità sviluppatore</b> (in alto a destra).</li>
      <li><b>Carica estensione non pacchettizzata</b> → scegliete la cartella scompattata.</li>
      <li>Fissate l'icona MK (puzzle → spillo), poi <b>Opzioni</b>: indirizzo del sito, token dell'estrattore (lo stesso di <code>ESTRATTORE_TOKEN</code> sul server), il vostro nome.</li>
      <li>Accendete <b>Estrazione automatica</b>. Se compare un captcha, la finestra dell'estrattore torna in primo piano con una notifica: risolvetelo e continua da solo.</li>
    </ol>
    <p className="imp-nota">Sulla pagina di un annuncio, l'icona MK ha anche «Aggiungi a MK Client» con la scelta del cliente. Dettagli: docs/estensione.md nel repository.</p>
  </div>
);

// Impostazioni → Estrattore → "Ricerca per area" (backend/verifica_area.py): la ricerca dentro la zona disegnata
// sulla mappa si accende da sola sul portale dove la verifica l'ha confermata; finché non è verificata (o se non
// funziona) si continua a cercare comune per comune.
const STATI_AREA = {
  confermata: { testo: 'confermata', tag: 't-g' },
  non_funziona: { testo: 'non funziona', tag: 't-r' },
  non_verificabile: { testo: 'non verificata', tag: 't-w' },
};

export const RicercaArea = () => {
  const [dati, setDati] = useState(null);
  const [attesa, setAttesa] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/verifica-area`).then((r) => setDati(r.data)).catch(() => {}); }, []);
  const lancia = async () => {
    setAttesa(true);
    try {
      const r = await axios.post(`${API}/admin/verifica-area/lancia`);
      setDati(r.data);
      if (r.data.messe) toast.success(`${r.data.messe === 1 ? '1 pagina' : `${r.data.messe} pagine`} in coda: l'estensione le apre una alla volta, con le sue pause`);
      else toast.error(r.data.motivo === 'ci sono già verifiche in coda' ? 'Ci sono già verifiche in coda' : 'Nessun cliente con la zona disegnata sulla mappa e il budget');
    } catch { toast.error('Non riuscito'); } finally { setAttesa(false); }
  };
  if (!dati?.righe) return null;
  const pianificata = leggiDataUtc(dati.pianificata);
  return (
    <section className="card" data-testid="ricerca-area">
      <div className="ttl"><div className="i b"><Icona nome="mappa" /></div><h2>Ricerca per area</h2>
        {dati.in_coda > 0 && <span className="tag t-b">{dati.in_coda} in coda</span>}
        <button type="button" className="btn sm" disabled={attesa || dati.in_coda > 0} onClick={lancia}>{attesa ? 'Metto in coda…' : 'Verifica di nuovo'}</button></div>
      <p className="imp-nota">
        Dove funziona, per i clienti che disegnano la zona sulla mappa apriamo <b>una pagina dentro il poligono</b> invece di una per ogni comune.
        L'estensione (dalla {dati.versione_minima}) apre al massimo {dati.massimo} pagine ad area e controlla che gli annunci trovati stiano davvero
        nei comuni della zona; se un portale ci blocca o chiede un captcha la verifica si ferma lì. casa.it non ha la ricerca per area: là si cerca
        sempre comune per comune.
        {dati.con_zona === 0 ? ' Nessun cliente ha disegnato la zona: per ora non c\'è niente da verificare.'
          : ` ${dati.con_zona === 1 ? 'Un cliente ha' : `${dati.con_zona} clienti hanno`} la zona disegnata.`}
        {pianificata ? ` Ultima verifica messa in coda il ${giornoEMese(pianificata)}.` : ' Parte da sola la prima volta che l\'estensione 2.9.0 chiede lavoro.'}
      </p>
      <div className="tabella-box" style={{ marginTop: '.8rem' }}>
        <table className="tabella verifica">
          <thead><tr><th>Portale</th><th>Stato</th><th>Data</th><th>Perché</th></tr></thead>
          <tbody>
            {dati.righe.map((r) => {
              const s = STATI_AREA[r.esito];
              const data = leggiDataUtc(r.data);
              return (
                <tr key={r.portale} data-testid={`area-${r.portale}`}>
                  <td className="nowrap">{r.nome}<small>nel link: {r.parametro}</small></td>
                  <td className="nowrap">{s ? <span className={`tag ${s.tag}`}>{s.testo}</span> : <span className="muto">non verificata</span>}
                    <small>{r.accesa ? 'la cerchiamo dentro la zona' : 'cerchiamo comune per comune'}</small></td>
                  <td className="nowrap">{data ? giornoEMese(data) : '—'}
                    {r.totale !== null && r.totale !== undefined ? <small>{migliaia(r.totale)} risultati</small> : null}</td>
                  <td>{r.dettaglio || 'Mai provata: la prova la fa l\'estensione nel Chrome dell\'ufficio.'}
                    {r.portale === 'idealista' && r.accesa && <small>Il link ad area di idealista non porta il budget: gli annunci fuori budget li scarta il server.</small>}
                    {r.url && <small><a href={r.url} target="_blank" rel="noopener noreferrer">pagina verificata ↗</a></small>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const StatoEstrattore = () => {
  const [stato, setStato] = useState(null);
  const [istruzioni, setIstruzioni] = useState(false);
  useEffect(() => {
    let vivo = true;
    const carica = () => axios.get(`${API}/admin/estrattore`).then((r) => vivo && setStato(r.data)).catch(() => {});
    carica();
    const t = setInterval(carica, 60 * 1000);
    return () => { vivo = false; clearInterval(t); };
  }, []);
  if (!stato) return null;
  const fermo = !stato.attivo;
  const agente = stato.agente === 'estensione' ? `estensione Chrome${stato.versione ? ` ${stato.versione}` : ''}`
    : stato.agente === 'python' ? `programma Python${stato.versione ? ` ${stato.versione}` : ''}` : null;
  return (
    <section className={`card estrattore${fermo ? ' fermo' : ''}`} data-testid="estrattore-widget">
      <div className="ttl"><div className={`i${fermo ? ' r' : ' b'}`}><Icona nome="impostazioni" /></div>
        <h2 className="stato-est"><i aria-hidden="true" /><b>Estrattore: {rigaEstrattore(stato)}</b></h2>
        {/* il diario: cosa sta facendo adesso, la coda, cosa ha fatto (docs/estensione.md) */}
        <Link to="/admin/impostazioni#estrattore-attivita" data-testid="link-attivita">Attività →</Link>
        <button type="button" className="link" aria-expanded={istruzioni} onClick={() => setIstruzioni(!istruzioni)}>Istruzioni</button></div>
      <p className="imp-nota">{`· ${stato.in_coda} in coda · ${stato.fatti_oggi} ${stato.fatti_oggi === 1 ? 'letto' : 'letti'} oggi${agente ? ` · ${agente}` : ''}`}</p>
      {(stato.portali_fermi || []).length > 0 && (
        <p className="imp-nota w" data-testid="portali-fermi">Fermo fino a domani su {stato.portali_fermi.join(', ')} (tre captcha oggi).</p>
      )}
      {stato.captcha_in_attesa?.length > 0 && (
        <div className="avviso" data-testid="captcha-in-attesa">
          {stato.captcha_in_attesa.map((c) => (
            <p key={c.id || c.immobile_id}>Captcha su {c.portale} da {minuti(c.da_minuti)}: risolvetelo nella finestra dell'estrattore sul computer dell'ufficio, poi continua da solo.</p>
          ))}
        </div>
      )}
      {/* La coda «bozze da confermare» non c'è più: quello che l'estrattore legge è già completo e si corregge
          da «Modifica» come qualunque altra casa. Resta solo quello che NON è riuscito a leggere. */}
      <div className="liste">
        <div>
          <p className="imp-gruppo">Non riusciti, da compilare a mano</p>
          {stato.non_riusciti.length === 0 && <p className="vuoto">Nessuno</p>}
          {stato.non_riusciti.map((i) => (
            <Link key={i.id} to={linkImmobile(i)}>{i.titolo || 'Annuncio da leggere'}<small>{i.cliente_nome} · Compila dati</small>{i.errore && <small className="bad">{i.errore}</small>}</Link>
          ))}
        </div>
      </div>
      {istruzioni && <IstruzioniEstensione />}
    </section>
  );
};

const EstrattoreWidget = () => (
  <>
    <StatoEstrattore />
    <RicercaArea />
  </>
);

export default EstrattoreWidget;
